import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { normalizeInternalAppRedirect } from 'src/app/core/utils/internal-app-redirect';
import {
  AUTH_FLOWS,
  findStepIdByRoute,
  getFlowStepConfig,
} from '../config/auth-flow.config';
import {
  AuthFlowId,
  AuthStepId,
  AuthTransitionKey,
  AuthFlowComplete,
  StepNavigatePayload,
} from '../models/auth-flow.models';
import { AuthSessionStore } from './auth-session.store';

@Injectable({ providedIn: 'root' })
export class AuthFlowService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly store = inject(AuthSessionStore);

  flowId(): AuthFlowId {
    return this.store.flowId();
  }

  initFlow(
    flowId: AuthFlowId,
    pvzUuid: string | null = null,
    redirect: string | null = null,
  ): void {
    const prevFlowId = this.store.flowId();
    const currentLogin = this.store.account().login;
    // Не сбрасываем login между login ↔ registration.
    // Важно и при remount того же flowId: таб меняет flowId через
    // replaceState без remount, а переход на /code уже создаёт новый shell.
    const preserveLogin =
      Boolean(currentLogin) &&
      this.isLoginOrRegistration(flowId) &&
      this.isLoginOrRegistration(prevFlowId);

    this.store.reset(flowId);
    this.store.flowId.set(flowId);

    if (preserveLogin) {
      this.store.patchAccount({ login: currentLogin });
    }

    if (this.isLoginOrRegistration(flowId)) {
      this.store.redirectUrl.set(normalizeInternalAppRedirect(redirect));
    }

    if (pvzUuid) {
      this.store.setPvzUuid(pvzUuid);
    }
  }

  /**
   * Переключение между входом и регистрацией
   * @param flowId - идентификатор потока
   * @returns {void} - void, который разрешается после переключения
   */
  switchBetweenLoginAndRegistration(
    flowId: AuthFlowId.Login | AuthFlowId.Registration,
  ): void {
    if (this.store.flowId() === flowId) {
      return;
    }

    this.store.flowId.set(flowId);
    this.store.isNewUser.set(flowId === AuthFlowId.Registration);

    const path =
      flowId === AuthFlowId.Registration ? '/registration' : '/login';
    const query = this.location.path(true).split('?')[1]?.split('#')[0] ?? '';
    this.location.replaceState(path, query);
  }

  currentStepId(): AuthStepId | null {
    const routeSegment = this.getCurrentRouteSegment();
    if (!routeSegment) {
      return AUTH_FLOWS[this.flowId()].initialStep;
    }
    return findStepIdByRoute(this.flowId(), routeSegment);
  }

  getBaseCommands(): string[] {
    const flowId = this.flowId();
    if (flowId === AuthFlowId.Pvz && this.store.pvzUuid()) {
      return ['/pvz-link', this.store.pvzUuid()!];
    }
    if (flowId === AuthFlowId.Registration) {
      return ['/registration'];
    }
    return ['/login'];
  }

  navigateToStep(
    stepId: AuthStepId,
    extras?: { queryParams?: Record<string, string> },
  ): void {
    const flow = AUTH_FLOWS[this.flowId()];
    const step = getFlowStepConfig(this.flowId(), stepId);
    if (!step) {
      return;
    }

    const commands =
      stepId === flow.initialStep
        ? this.getBaseCommands()
        : [...this.getBaseCommands(), step.route];

    this.router.navigate(commands, {
      queryParams: extras?.queryParams,
      queryParamsHandling: extras?.queryParams ? 'merge' : 'preserve',
    });
  }

  navigateToInitialStep(): void {
    this.navigateToStep(AUTH_FLOWS[this.flowId()].initialStep);
  }

  goBack(): void {
    const stepId = this.currentStepId();
    if (!stepId) {
      this.navigateToInitialStep();
      return;
    }

    const back = getFlowStepConfig(this.flowId(), stepId)?.back;
    if (!back) {
      return;
    }

    if (back === AuthStepId.ENTER_EMAIL) {
      this.store.patchAccount({ attempts: null, code: '' });
    }

    this.navigateToStep(back);
  }

  goNext(
    payload: StepNavigatePayload,
  ): AuthStepId | AuthFlowComplete | null {
    const nextStep = this.getNextStep(payload);

    if (!nextStep || nextStep === AuthFlowComplete.Complete) {
      return nextStep;
    }

    this.navigateToStep(nextStep);
    return nextStep;
  }

  getNextStep(
    payload: StepNavigatePayload,
  ): AuthStepId | AuthFlowComplete | null {
    // После login↔registration через replaceState router.url может отставать —
    // тогда currentStepId() = null, хотя UI на начальном шаге.
    const stepId =
      this.currentStepId() ?? AUTH_FLOWS[this.flowId()].initialStep;

    const stepConfig = getFlowStepConfig(this.flowId(), stepId);
    const nextStep =
      stepConfig?.next[payload.transition] ??
      stepConfig?.next[AuthTransitionKey.Default];

    return nextStep ?? null;
  }

  /**
   * Проверяет, нужно ли показывать футер с правовыми документами
   * @returns {boolean} true, если нужно показывать футер, false в противном случае
   */
  shouldShowLegalFooter(): boolean {
    const stepId = this.currentStepId();
    if (!stepId) {
      return false;
    }
    return Boolean(
      getFlowStepConfig(this.flowId(), stepId)?.showLegalFooter,
    );
  }

  private getCurrentRouteSegment(): string | null {
    const path = this.router.url.split('?')[0];
    const segments = path.split('/').filter(Boolean);
    const baseSegments = this.getBaseCommands()
      .flatMap((command) => command.split('/'))
      .filter(Boolean);

    // Таб login↔registration меняет URL через Location.replaceState,
    // но не обновляет Router.url. Тогда segments=['login'], а
    // baseSegments=['registration'] (или наоборот) — это всё ещё
    // начальный шаг, а не неизвестный route.
    if (segments.length <= baseSegments.length) {
      return null;
    }

    return segments[segments.length - 1] ?? null;
  }

  private isLoginOrRegistration(flowId: AuthFlowId): boolean {
    return flowId === AuthFlowId.Login || flowId === AuthFlowId.Registration;
  }
}
